import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorReason, FlashscanResult } from "@/types/Flashscan";
import {
  FlashscanAnomaliesFilterValue,
  FlashscanAnomaliesFilters,
} from "@/components/FlashscanAnomaliesFilters/FlashscanAnomaliesFilters";
import { FlashscanManualResolutionModal } from "@/components/FlashscanManualResolutionModal/FlashscanManualResolutionModal";
import { FlashscanOrphanSaleModal } from "@/components/FlashscanOrphanSaleModal/FlashscanOrphanSaleModal";
import { FlashscanOrphanBillModal } from "@/components/FlashscanOrphanBillModal/FlashscanOrphanBillModal";
import { ReactComponent as NewWindowIcon } from "@/assets/icons/new-window.svg";
import "./FlashscanAnomaliesTable.scss";

type FlashscanAnomaliesTableProps = {
  flashscanResult: FlashscanResult;
  onResolved?: () => Promise<void> | void;
};

const formatInfosLine = (line: string) => {
  // Format attendu (backend): "[Sat Feb 01 2025, Sat Mar 01 2025]: 1824€"
  const match = line.match(/^\[(.+?),\s*(.+?)\]:\s*([\d.,]+)\s*€?\s*$/);
  if (!match) return line.trim();

  const [, startRaw, endRaw, amountRaw] = match;

  const startDate = new Date(startRaw);
  const endDate = new Date(endRaw);

  const formatDate = (d: Date) =>
    Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("fr-FR");

  const start = formatDate(startDate);
  const end = formatDate(endDate);

  const amount = Number(String(amountRaw).replace(",", "."));
  const amountFormatted = Number.isNaN(amount)
    ? amountRaw
    : amount.toLocaleString("fr-FR");

  if (!start || !end) return line.trim();

  return `${start} → ${end} : ${amountFormatted} €`;
};

const formatAdditionalInfos = (additionalInfos?: string) => {
  const raw = (additionalInfos ?? "").trim();
  if (!raw) return null;

  const lines = raw
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map(formatInfosLine);

  return lines.length ? lines : null;
};

export const FlashscanAnomaliesTable: React.FC<
  FlashscanAnomaliesTableProps
> = ({ flashscanResult, onResolved }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FlashscanAnomaliesFilterValue>("ALL");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOrphanSaleModalOpen, setIsOrphanSaleModalOpen] = useState(false);
  const [isOrphanBillModalOpen, setIsOrphanBillModalOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const availableReasons = useMemo(() => {
    const present = new Set<ErrorReason>();
    for (const group of flashscanResult.inErrorSales) {
      if (!group.sales?.length) continue;
      present.add(group.errorReason as ErrorReason);
    }

    // Ordre produit (ancien ordre hardcodé), mais filtré par présence réelle
    const order: ErrorReason[] = [
      "ORPHAN_SALE",
      "ORPHAN_BILLS",
      "MISSING_PERIOD",
      "WRONG_AMOUNT",
      "LAST_BILL_LATE",
    ];

    return order.filter((r) => present.has(r));
  }, [flashscanResult.inErrorSales]);

  const rows = useMemo(() => {
    const items = flashscanResult.inErrorSales
      .flatMap((group) => group.sales.map((sale) => ({ group, sale })))
      .filter(({ group }) => {
        if (filter === "ALL") return true;
        return group.errorReason === filter;
      })
      .sort((a, b) => b.sale.amount - a.sale.amount);

    return items;
  }, [flashscanResult.inErrorSales, filter]);

  // Si le filtre courant n'existe plus (ex: après résolution), revenir à ALL
  useEffect(() => {
    if (filter === "ALL") return;
    if (!availableReasons.includes(filter)) setFilter("ALL");
  }, [availableReasons, filter]);

  const selected = useMemo(() => {
    if (!selectedKey) return null;
    const found = rows.find(
      ({ group, sale }) => `${group.errorReason}-${sale.id}` === selectedKey,
    );
    if (!found) return null;

    const { group, sale } = found;
    const entityType = group.errorReason === "ORPHAN_BILLS" ? "BILL" : "SALE";
    const title =
      sale.name && sale.accountName
        ? `${sale.name} - ${sale.accountName}`
        : (sale.name ?? sale.accountName ?? "-");

    return {
      id: sale.id,
      errorReason: group.errorReason as ErrorReason,
      title,
      providerId: sale.providerId,
      impactAmount: sale.amount,
      additionalInfos: sale.additionalInfos,
      wrongAmountComparison: sale.wrongAmountComparison,
      entityType,
      closeDate: sale.closeDate,
      accountName: sale.accountName,
      name: sale.name,
      orphanBillDetails: sale.orphanBillDetails,
    } as const;
  }, [rows, selectedKey]);

  return (
    <section className="flashscan-anomalies-table">
      <h2 className="flashscan-anomalies-table-title">
        {t("pages.flashscan.anomalies.title")}
      </h2>

      <div className="flashscan-anomalies-table-separator" />

      <FlashscanAnomaliesFilters
        value={filter}
        onChange={setFilter}
        availableReasons={availableReasons}
      />

      <div className="flashscan-anomalies-table-grid">
        <div className="flashscan-anomalies-table-head">
          <div>{t("pages.flashscan.anomalies.columns.impact")}</div>
          <div>{t("pages.flashscan.anomalies.columns.rule")}</div>
          <div>{t("pages.flashscan.anomalies.columns.dealClient")}</div>
          <div>{t("pages.flashscan.anomalies.columns.additionalInfos")}</div>
          <div>{t("pages.flashscan.anomalies.columns.action")}</div>
        </div>

        <div className="flashscan-anomalies-table-body">
          {rows.map(({ group, sale }) => (
            <div
              key={`${group.errorReason}-${sale.id}`}
              className="flashscan-anomalies-table-row"
            >
              <div className="flashscan-anomalies-table-impact">
                {sale.amount.toLocaleString("fr-FR")} €
              </div>
              <div>
                {t(`pages.flashscan.rules.${group.errorReason as ErrorReason}`)}
              </div>
              <div className="flashscan-anomalies-table-deal-client">
                <div className="flashscan-anomalies-table-deal-client-title">
                  {sale.name && sale.accountName
                    ? `${sale.name} - ${sale.accountName}`
                    : (sale.name ?? sale.accountName ?? "-")}
                </div>
                <div className="flashscan-anomalies-table-deal-client-subtitle">
                  {`${t("pages.flashscan.anomalies.sourceId")}: ${sale.providerId}`}
                </div>
              </div>
              <div className="flashscan-anomalies-table-additional">
                {formatAdditionalInfos(sale.additionalInfos)?.map((l, idx) => (
                  <div
                    key={idx}
                    className="flashscan-anomalies-table-additional-line"
                  >
                    {l}
                  </div>
                )) || "-"}
              </div>
              <div className="flashscan-anomalies-table-action">
                <button
                  type="button"
                  className="flashscan-anomalies-table-action-button"
                  onClick={() => {
                    setSelectedKey(`${group.errorReason}-${sale.id}`);
                    if (group.errorReason === "ORPHAN_SALE") {
                      setIsOrphanSaleModalOpen(true);
                    } else if (group.errorReason === "ORPHAN_BILLS") {
                      setIsOrphanBillModalOpen(true);
                    } else {
                      setIsModalOpen(true);
                    }
                  }}
                  aria-label={t("pages.flashscan.manualResolution.open")}
                >
                  <NewWindowIcon aria-hidden="true" focusable="false" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <FlashscanManualResolutionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        target={selected}
        onResolved={async () => {
          await onResolved?.();
        }}
      />

      <FlashscanOrphanSaleModal
        isOpen={isOrphanSaleModalOpen}
        onClose={() => setIsOrphanSaleModalOpen(false)}
        target={
          selected?.errorReason === "ORPHAN_SALE"
            ? {
                id: selected.id,
                providerId: selected.providerId,
                name: selected.name,
                accountName: selected.accountName ?? "",
                amount: selected.impactAmount,
                closeDate: selected.closeDate,
              }
            : null
        }
        onResolved={async () => {
          await onResolved?.();
        }}
      />

      <FlashscanOrphanBillModal
        isOpen={isOrphanBillModalOpen}
        onClose={() => setIsOrphanBillModalOpen(false)}
        target={
          selected?.errorReason === "ORPHAN_BILLS"
            ? {
                id: selected.id,
                providerId: selected.providerId,
                accountName: selected.accountName ?? "",
                amount: selected.impactAmount,
                startPeriod: selected.orphanBillDetails?.startPeriod,
                endPeriod: selected.orphanBillDetails?.endPeriod,
                soldItemsDescription:
                  selected.orphanBillDetails?.soldItemsDescription,
              }
            : null
        }
        onResolved={async () => {
          await onResolved?.();
        }}
      />
    </section>
  );
};
